import { Stream } from "kefir";
import QuillDelta from "quill-delta";

import KefirBus from "./KefirBus";
import {
  SubjectAttribute,
  AttributeKeyStrings,
  AttributeKey,
  PartialUnknownShape,
} from "./types/attributes";
import {
  WBEventAppendSubjectAttributeSinglePayload,
  WBEventUpsertSubjectAttributeSinglePayload,
  WBEventUpsertSubjectAttributeManyPayload,
  WBEventDeleteSubjectPayload,
  WBEventOuter,
  WBEventAnyPayload,
  WBEventOuterServer,
} from "./types/WBEvent2";
import { OldEvent } from "./types/WBEvent";
import { Verb } from "./types/Verb";
import { Scope } from "./types/Scope";
import {
  ShapeWithData,
  ShapeWithCanvasBasedDOMRepresentation,
  Shape2,
} from "./types/Shape";
import { WebsocketController } from "./WebsocketController";
import { throttleLeading } from "./util";

export interface VersionedAttribute {
  version: number;
  attribute: SubjectAttribute;
  clientID: string;
}

export enum SubjectMessageType {
  Upsert = "Upsert",
  Delete = "Delete",
}

export interface SubjectMessageUpsert {
  type: SubjectMessageType.Upsert;
  subjectID: string;
  attributes: Shape2;
  isFromMyself: boolean;
}

export interface SubjectMessageDelete {
  type: SubjectMessageType.Delete;
  subjectID: string;
  isFromMyself: boolean;
}

export type SubjectMessageAny = SubjectMessageUpsert | SubjectMessageDelete;

/// old format to new format
function translateKey(k: string): string {
  switch (k) {
    case "kind":
      return "shapeType";
    case "a":
      return "pointA";
    case "b":
      return "pointB";
    case "aCap":
      return "lineCapA";
    case "bCap":
      return "lineCapB";
    case "color":
      return "strokeColor";
    case "width":
      return "strokeWidth";
    case "topLeft":
      return "boundingBoxOrigin";
    case "originalSize":
      return "boundingBoxOriginalSize";
    default:
      return k;
  }
}

function isQuillDelta(obj: unknown): obj is QuillDelta {
  return !!(obj as { ops?: unknown[] }).ops;
}

/// slightly not-type-safe way to build an attribute list concisely
export function makeAttributeList(
  values: Record<string, unknown>,
  extraAttributes = new Array<SubjectAttribute>()
): SubjectAttribute[] {
  const omit = new Set<AttributeKey>();
  for (const a of extraAttributes) {
    omit.add(a.key);
  }

  const val = new Array<SubjectAttribute>();
  for (const k in values) {
    const k2 = AttributeKeyStrings[translateKey(k)];
    if (!k2) {
      console.error("Bad attribute key:", k);
      continue;
    }
    const key = translateKey(k) as AttributeKey;
    if (omit.has(key)) continue;
    val.push({
      key,
      // eslint-ignore-next-line
      value: values[k],
    });
  }
  return val.concat(extraAttributes);
}

export class ShapeStore {
  attributes: Record<string, Record<string, VersionedAttribute>> = {};

  websocketController?: WebsocketController;
  changes: Stream<SubjectMessageAny, void>;
  // TODO: break out ShapeStoreLegacyShim
  legacyPoints: Record<string, [number, number][]> = {};
  legacyText: Record<string, QuillDelta> = {};
  legacyDataIDToSubjectID: Record<string, string> = {};
  deletedShapes = new Set<string>();
  private bus: KefirBus<SubjectMessageAny, void>;
  userChangeEvents = new KefirBus<void, void>("ShapeStore.userChangeEvents");
  clearEvents = new KefirBus<void, void>("ShapeStore.clearEvents");
  undoClearEvents = new KefirBus<void, void>("ShapeStore.undoClearEvents");

  private _sendUpsertSingleThrottled: (
    scope: Scope.Live,
    subjectID: string,
    attribute: SubjectAttribute
  ) => void;
  private _sendUpsertManyThrottled: (
    scope: Scope.Live,
    subjectID: string,
    attributes: PartialUnknownShape
  ) => void;

  constructor(
    private clientID: string,
    private wb: { wbid: string; makeID: () => string }
  ) {
    this.bus = new KefirBus("ShapeStore.changes");
    this.changes = this.bus.stream;
    // eslint-disable-next-line
    (window as any).shapeStore = this;

    this._sendUpsertManyThrottled = throttleLeading(
      this,
      300,
      this.sendUpsertMany
    );
    this._sendUpsertSingleThrottled = throttleLeading(
      this,
      300,
      this.sendUpsertSingle
    );
  }

  reset() {
    this.attributes = {};
    this.legacyPoints = {};
    this.legacyText = {};
    this.legacyDataIDToSubjectID = {};
    this.deletedShapes = new Set();
  }

  teardown() {
    this.websocketController?.eventStream.offValue(this.applyOmniServerEvent);
  }

  replaceWebsocketController(wc: WebsocketController) {
    this.websocketController?.eventStream.offValue(this.applyOmniServerEvent);
    this.websocketController = wc;
    this.websocketController.eventStream.onValue(this.applyOmniServerEvent);
  }

  private applyOmniServerEvent = (msg: WBEventOuterServer | OldEvent) => {
    if ((msg as OldEvent).verb) {
      this.applyLegacyEvent(msg as OldEvent);
    } else {
      this.apply(msg as WBEventOuterServer);
    }
  };

  private dispatch(subjectMessage: SubjectMessageAny) {
    this.bus.emit(subjectMessage);
  }

  private ensureExists(subjectID: string) {
    if (!this.attributes[subjectID]) {
      this.attributes[subjectID] = {};
    }
  }

  private collectAttributes(subjectID: string): Shape2 {
    const val: Record<string, unknown> = {};
    for (const attrKey of Object.keys(this.attributes[subjectID])) {
      const attr = this.attributes[subjectID][attrKey];
      val[attr.attribute.key] = attr.attribute.value;
    }
    return val as unknown as Shape2;
  }

  getIsAllowed(
    subjectID: string,
    version: number,
    attributes: SubjectAttribute[]
  ): boolean {
    const subjAttrs: Record<string, VersionedAttribute> =
      this.attributes[subjectID];
    if (!subjAttrs) return true;
    for (const a of attributes) {
      const myVersion: VersionedAttribute = subjAttrs[a.key];
      if (!myVersion) continue;
      if (myVersion.version >= version) {
        // console.warn("Discarding outdated updates to", subjectID, attributes);
        return false;
      }
    }
    return true;
  }

  getAttributes(subjectID: string): SubjectAttribute[] {
    return Object.values(this.attributes[subjectID]).map((a) => a.attribute);
  }

  getNextVersion(subjectID: string, keys: AttributeKey[]): number {
    if (!this.attributes[subjectID]) return 0;
    let maxVersion = 0;
    for (const k of keys) {
      if (!this.attributes[subjectID][k]) continue;
      maxVersion = Math.max(maxVersion, this.attributes[subjectID][k].version);
    }
    return maxVersion + 1 + Math.random();
  }

  // eslint-disable-next-line
  getIsUndoable(events: WBEventAnyPayload): boolean {
    // TODO: return false if client id for any attribute doesn't match this.clientID
    return true;
  }

  send(scope: Scope, payload: WBEventAnyPayload, updateVersionNumber = true) {
    const event = {
      id: this.wb.makeID(),
      wbid: this.wb.wbid,
      clientID: this.clientID,
      scope: scope,
      payload: payload,
    };

    if (updateVersionNumber) {
      switch (payload.verb) {
        case Verb.UpsertSubjectAttributeMany:
          (event.payload as WBEventUpsertSubjectAttributeManyPayload).version =
            this.getNextVersion(
              payload.subjectID,
              payload.attributes.map(({ key }) => key)
            );
          break;
        case Verb.UpsertSubjectAttributeSingle:
          (
            event.payload as WBEventUpsertSubjectAttributeSinglePayload
          ).version = this.getNextVersion(payload.subjectID, [
            payload.attribute.key,
          ]);
          break;
        default:
          break;
      }
    }

    if (scope === Scope.Live || scope === Scope.Persist) {
      this.websocketController?.publish(event);
    }

    if (scope === Scope.Persist) {
      this.userChangeEvents.emit();
    }

    this.apply(event);
  }

  sendUpsertSingle(
    scope: Scope,
    subjectID: string,
    attribute: SubjectAttribute
  ) {
    this.send(scope, {
      verb: Verb.UpsertSubjectAttributeSingle,
      subjectID,
      version: -1,
      attribute: attribute,
    });
  }

  sendUpsertSingleLive(subjectID: string, attribute: SubjectAttribute) {
    this._sendUpsertSingleThrottled(Scope.Live, subjectID, attribute);
    this.sendUpsertSingle(Scope.Local, subjectID, attribute);
  }

  sendUpsertMany = (
    scope: Scope,
    subjectID: string,
    attributes: PartialUnknownShape
  ) => {
    this.send(scope, {
      verb: Verb.UpsertSubjectAttributeMany,
      subjectID,
      version: -1,
      attributes: makeAttributeList(attributes),
    });
  };

  sendUpsertManyLive(subjectID: string, attributes: PartialUnknownShape) {
    this._sendUpsertManyThrottled(Scope.Live, subjectID, attributes);
    this.sendUpsertMany(Scope.Local, subjectID, attributes);
  }

  sendAppend(scope: Scope, subjectID: string, attribute: SubjectAttribute) {
    this.send(scope, {
      verb: Verb.AppendSubjectAttributeSingle,
      subjectID,
      attribute,
      version: this.getNextVersion(subjectID, [attribute.key]),
    });
  }

  sendDelete(scope: Scope, subjectID: string) {
    this.send(scope, {
      verb: Verb.DeleteShape,
      subjectID,
    });
  }

  apply(event: WBEventOuter, guard = true) {
    if (guard) {
      try {
        // console.log(event);
        this._apply(event);
      } catch (error) {
        console.error("Error applying event:", event);
        console.error(error);
      }
    } else {
      this._apply(event);
    }
  }

  _apply(event: WBEventOuter) {
    switch (event.payload.verb) {
      case Verb.Clear:
        this.clearEvents.emit();
        // Map clear to individual delete events for all shapes
        for (const subjectID of Object.keys(this.attributes)) {
          this.applyDeleteSingle(
            {
              verb: Verb.DeleteShape,
              subjectID,
            },
            true
          );
        }
        break;
      case Verb.UndoClear:
        this.undoClearEvents.emit();
        break;
      case Verb.DeleteShape:
        this.applyDeleteSingle(event.payload, event.clientID === this.clientID);
        break;
      case Verb.UpsertSubjectAttributeSingle:
        if (event.payload.version < 0) {
          throw new Error("Shouldn't apply this event until it has a verson");
        }
        this.applyUpsertSingle(event.payload, event.clientID);
        break;
      case Verb.UpsertSubjectAttributeMany:
        if (event.payload.version < 0) {
          throw new Error("Shouldn't apply this event until it has a verson");
        }
        this.applyUpsertMany(event.payload, event.clientID);
        break;
      case Verb.AppendSubjectAttributeSingle:
        this.applyAppendSingle(event.payload, event.clientID);
        break;
      case Verb.SetIsLocked:
        break; // handled by WhiteboardController
      default:
        console.error("Unknown v2 event:", event);
    }
  }

  applyDeleteSingle(
    payload: WBEventDeleteSubjectPayload,
    isFromMyself: boolean
  ) {
    // don't actually delete the attributes. if a user resurrects it we want it in its
    // pre-deleted state, otherwise you'll end up with partial shapes.
    // delete this.attributes[payload.subjectID];
    this.deletedShapes.add(payload.subjectID);
    // it should be enough to just tell everyone to stop showing it.
    this.dispatch({
      type: SubjectMessageType.Delete,
      subjectID: payload.subjectID,
      isFromMyself,
    });
  }

  applyUpsertSingle(
    payload: WBEventUpsertSubjectAttributeSinglePayload,
    clientID: string | null = null
  ) {
    // if (this.deletedShapes.has(payload.subjectID)) {
    //   console.warn("Trying to edit deleted shape");
    //   return;
    // }
    if (
      !this.getIsAllowed(payload.subjectID, payload.version, [
        payload.attribute,
      ])
    ) {
      return;
    }

    this.ensureExists(payload.subjectID);

    this.attributes[payload.subjectID][payload.attribute.key] = {
      version: payload.version,
      attribute: payload.attribute,
      clientID: clientID || this.clientID,
    };
    this.dispatch({
      type: SubjectMessageType.Upsert,
      subjectID: payload.subjectID,
      attributes: this.collectAttributes(payload.subjectID),
      isFromMyself: clientID === this.clientID || clientID === null,
    });
  }

  applyUpsertMany(
    payload: WBEventUpsertSubjectAttributeManyPayload,
    clientID: string | null
  ) {
    // if (this.deletedShapes.has(payload.subjectID)) {
    //   console.warn("Trying to edit deleted shape");
    //   return;
    // }
    if (
      !this.getIsAllowed(payload.subjectID, payload.version, payload.attributes)
    ) {
      return;
    }

    this.ensureExists(payload.subjectID);
    for (const attr of payload.attributes) {
      this.attributes[payload.subjectID][attr.key] = {
        version: payload.version,
        attribute: attr,
        clientID: clientID || this.clientID,
      };
    }
    this.dispatch({
      type: SubjectMessageType.Upsert,
      subjectID: payload.subjectID,
      attributes: this.collectAttributes(payload.subjectID),
      isFromMyself: clientID === this.clientID || clientID === null,
    });
  }

  applyAppendSingle(
    payload: WBEventAppendSubjectAttributeSinglePayload,
    clientID: string | null = null
  ) {
    if (
      !this.getIsAllowed(payload.subjectID, payload.version, [
        payload.attribute,
      ])
    ) {
      return;
    }

    const k = payload.attribute.key;
    const v = payload.attribute.value;

    // if (this.deletedShapes.has(payload.subjectID)) {
    //   console.warn("Trying to edit deleted shape");
    //   return;
    // }
    this.ensureExists(payload.subjectID);

    const a = this.attributes[payload.subjectID][k];
    if (!a) {
      this.attributes[payload.subjectID][k] = {
        attribute: payload.attribute,
        version: 0,
        clientID: clientID || this.clientID,
      };
    } else if (Array.isArray(v)) {
      const array = a.attribute.value as Array<unknown>;
      array.push(...(v as Array<unknown>));
      a.clientID = clientID || this.clientID;
    } else if (isQuillDelta(v)) {
      const oldDelta = new QuillDelta(a.attribute.value as QuillDelta);
      a.attribute.value = oldDelta.concat(v);
    } else {
      console.error("Unknown append data type:", payload.attribute.value);
      return;
    }
    this.dispatch({
      type: SubjectMessageType.Upsert,
      subjectID: payload.subjectID,
      attributes: this.collectAttributes(payload.subjectID),
      isFromMyself: clientID === this.clientID || clientID === null,
    });
  }

  applyLegacyEvent(event: OldEvent) {
    switch (event.verb) {
      case Verb.Clear:
        this.clearEvents.emit();
        break;
      case Verb.UndoClear:
        this.undoClearEvents.emit();
        break;
      case Verb.MakeData:
        this.handleLegacyMakeData(event);
        break;
      case Verb.AppendData:
        this.handleLegacyAppendData(event);
        break;
      case Verb.DeleteData:
        break;
      case Verb.UpsertShape:
        this.handleLegacyUpsertShape(event);
        break;
      case Verb.DeleteShape:
        this.handleLegacyDeleteShape(event);
        break;
      default:
        console.error("Unknown v1 event:", event);
    }
  }

  handleLegacyUpsertShape(event: OldEvent) {
    if (event.verb != Verb.UpsertShape) {
      console.error("Called handleLegacyUpsertShape with the wrong verb");
      return;
    }

    const eventContents: Record<string, unknown> = { ...event.contents };

    const extraAttributes: Record<
      string,
      [number, number] | [number, number][] | QuillDelta
    > = {};

    const dataID = (event.contents as unknown as ShapeWithData).dataID;
    if (dataID) {
      this.legacyDataIDToSubjectID[dataID] = event.subjectID;
      extraAttributes[AttributeKey.Points] =
        this.legacyPoints[dataID] || this.legacyText[dataID];
      delete eventContents.dataID;
    }

    const topLeft = (event.contents as ShapeWithCanvasBasedDOMRepresentation)
      .topLeft as [number, number];
    const bottomRight = (
      event.contents as ShapeWithCanvasBasedDOMRepresentation
    ).bottomRight as [number, number];
    if (topLeft && bottomRight) {
      const x: number = bottomRight[0] - topLeft[0];
      const y: number = bottomRight[1] - topLeft[1];
      extraAttributes[AttributeKey.BoundingBoxSize] = [x, y];
      delete eventContents.bottomRight;
    }

    const payload: WBEventUpsertSubjectAttributeManyPayload = {
      verb: Verb.UpsertSubjectAttributeMany,
      version: Date.now(),
      subjectID: event.subjectID,
      attributes: makeAttributeList({
        ...eventContents,
        ...extraAttributes,
      }),
    };

    this.applyUpsertMany(payload, event.clientID);
  }

  handleLegacyDeleteShape(event: OldEvent) {
    if (event.verb != Verb.DeleteShape) {
      console.error("Called handleLegacyUpsertShape with the wrong verb");
      return;
    }
    this.applyDeleteSingle(
      {
        subjectID: event.subjectID,
        verb: Verb.DeleteShape,
      },
      event.clientID === this.clientID
    );
  }

  handleLegacyMakeData(event: OldEvent) {
    if (event.verb != Verb.MakeData) {
      console.error("Called handleLegacyMakeData with the wrong verb");
      return;
    }

    if (Array.isArray(event.contents)) {
      this.legacyPoints[event.subjectID] = event.contents as [number, number][];
      // Update shape if it exists
      if (this.legacyDataIDToSubjectID[event.subjectID]) {
        this.applyUpsertSingle(
          {
            subjectID: this.legacyDataIDToSubjectID[event.subjectID],
            verb: Verb.UpsertSubjectAttributeSingle,
            version: Date.now(),
            attribute: {
              key: AttributeKey.Points,
              value: event.contents as [number, number][],
            },
          },
          event.clientID
        );
      }
    } else {
      this.legacyText[event.subjectID] = event.contents as QuillDelta;
      // Update shape if it exists
      if (this.legacyDataIDToSubjectID[event.subjectID]) {
        this.applyUpsertSingle(
          {
            subjectID: this.legacyDataIDToSubjectID[event.subjectID],
            verb: Verb.UpsertSubjectAttributeSingle,
            version: Date.now(),
            attribute: {
              key: AttributeKey.Text,
              value: event.contents as QuillDelta,
            },
          },
          event.clientID
        );
      }
    }
  }

  handleLegacyAppendData(event: OldEvent) {
    if (event.verb != Verb.AppendData) {
      console.error("Called handleLegacyMakeData with the wrong verb");
      return;
    }

    if (!this.legacyDataIDToSubjectID[event.subjectID]) {
      console.warn("No subject found for data", event.subjectID);
      return;
    }

    // don't worry about text case, this verb wasn't used for that
    this.applyAppendSingle(
      {
        subjectID: this.legacyDataIDToSubjectID[event.subjectID],
        verb: Verb.AppendSubjectAttributeSingle,
        version: Date.now(),
        attribute: {
          key: AttributeKey.Points,
          value: event.contents as [number, number][],
        },
      },
      event.clientID
    );
  }
}
